/**
 * SRS-22r (Scoliosis Research Society - 22 revised)
 * Cuestionario específico para pacientes con escoliosis
 * 
 * NOTA LEGAL: El SRS-22r es propiedad de la Scoliosis Research Society.
 * Los textos oficiales de las preguntas requieren licencia.
 * Esta implementación solo define la ESTRUCTURA, los textos
 * deben configurarse desde admin cuando se tenga autorización.
 * 
 * ESTRUCTURA:
 * - 22 ítems con respuesta tipo Likert (1-5)
 * - 5 dominios:
 *   1. Función/Actividad (items 5, 9, 12, 15, 18) - 5 items
 *   2. Dolor (items 1, 2, 8, 11, 17) - 5 items
 *   3. Autoimagen/Apariencia (items 4, 6, 10, 14, 19) - 5 items
 *   4. Salud Mental (items 3, 7, 13, 16, 20) - 5 items
 *   5. Satisfacción con el tratamiento (items 21, 22) - 2 items
 * 
 * PUNTUACIÓN:
 * - Cada ítem: 1 (peor) a 5 (mejor)
 * - Algunos ítems requieren inversión (reverse-scored)
 * - Puntuación por dominio: media de ítems respondidos del dominio
 * - Puntuación total: media de los 5 dominios (o media de 22 ítems)
 * - Rango: 1.0 (peor) a 5.0 (mejor)
 */

// Configuración de dominios SRS-22r
export const SRS22R_DOMAINS = {
  function_activity: {
    id: 'function_activity',
    name: 'Función/Actividad',
    nameShort: 'Función',
    items: [5, 9, 12, 15, 18],
    color: '#3B82F6', // blue
    description: 'Evalúa la capacidad funcional y nivel de actividad física'
  },
  pain: {
    id: 'pain',
    name: 'Dolor',
    nameShort: 'Dolor',
    items: [1, 2, 8, 11, 17],
    color: '#EF4444', // red
    description: 'Evalúa la intensidad y frecuencia del dolor'
  },
  self_image: {
    id: 'self_image',
    name: 'Autoimagen/Apariencia',
    nameShort: 'Imagen',
    items: [4, 6, 10, 14, 19],
    color: '#8B5CF6', // purple
    description: 'Evalúa la percepción de la imagen corporal'
  },
  mental_health: {
    id: 'mental_health',
    name: 'Salud Mental',
    nameShort: 'Mental',
    items: [3, 7, 13, 16, 20],
    color: '#10B981', // green
    description: 'Evalúa el estado emocional y bienestar psicológico'
  },
  satisfaction: {
    id: 'satisfaction',
    name: 'Satisfacción con el Tratamiento',
    nameShort: 'Satisfacción',
    items: [21, 22],
    color: '#F59E0B', // amber
    description: 'Evalúa la satisfacción con el manejo/tratamiento recibido'
  }
};

// Ítems que requieren inversión (reverse-scored)
// En SRS-22r, la mayoría ya están orientados: 5=mejor, 1=peor
// Ajustar según la versión oficial si es necesario
export const SRS22R_REVERSE_ITEMS = [];

// Configuración por defecto (textos placeholder - NO SON LOS OFICIALES)
export const SRS22R_CONFIG = {
  // Versión del cuestionario
  version: 'SRS-22r',
  
  // Rango de respuestas por defecto
  minValue: 1,
  maxValue: 5,
  
  // Método de cálculo del score total
  // 'domain_mean': media de los 5 dominios
  // 'item_mean': media de los 22 ítems
  totalScoreMethod: 'domain_mean',
  
  // Incluir dominio de satisfacción en el score total
  includeSatisfactionInTotal: false, // Por defecto no, ya que es específico del tratamiento
  
  // Días para advertir de cuestionario reciente
  recentDaysWarning: 42, // ~6 semanas
  
  // Textos configurables (placeholder - NO SON LOS OFICIALES)
  texts: {
    title: 'SRS-22r (Escoliosis)',
    fullTitle: 'Cuestionario SRS-22r de la Scoliosis Research Society',
    instructions: 'Por favor, responda a las siguientes preguntas sobre su espalda y cómo le afecta en su vida diaria. Seleccione la opción que mejor describa su situación.',
    licenseNote: 'Este instrumento es propiedad de la Scoliosis Research Society. Los textos oficiales requieren autorización.',
    
    // Opciones de respuesta por defecto (placeholder)
    defaultOptions: [
      { value: 1, label: 'Opción 1 (peor)' },
      { value: 2, label: 'Opción 2' },
      { value: 3, label: 'Opción 3' },
      { value: 4, label: 'Opción 4' },
      { value: 5, label: 'Opción 5 (mejor)' }
    ],
    
    // Los 22 ítems (textos placeholder - reemplazar con oficiales)
    items: {
      item1: {
        id: 1,
        domain: 'pain',
        text: '[Ítem 1 - Dolor: Texto oficial pendiente de licencia]',
        reverseScored: false,
        options: null // Usar defaultOptions
      },
      item2: {
        id: 2,
        domain: 'pain',
        text: '[Ítem 2 - Dolor: Texto oficial pendiente de licencia]',
        reverseScored: false,
        options: null
      },
      item3: {
        id: 3,
        domain: 'mental_health',
        text: '[Ítem 3 - Salud Mental: Texto oficial pendiente de licencia]',
        reverseScored: false,
        options: null
      },
      item4: {
        id: 4,
        domain: 'self_image',
        text: '[Ítem 4 - Autoimagen: Texto oficial pendiente de licencia]',
        reverseScored: false,
        options: null
      },
      item5: {
        id: 5,
        domain: 'function_activity',
        text: '[Ítem 5 - Función: Texto oficial pendiente de licencia]',
        reverseScored: false,
        options: null
      },
      item6: {
        id: 6,
        domain: 'self_image',
        text: '[Ítem 6 - Autoimagen: Texto oficial pendiente de licencia]',
        reverseScored: false,
        options: null
      },
      item7: {
        id: 7,
        domain: 'mental_health',
        text: '[Ítem 7 - Salud Mental: Texto oficial pendiente de licencia]',
        reverseScored: false,
        options: null
      },
      item8: {
        id: 8,
        domain: 'pain',
        text: '[Ítem 8 - Dolor: Texto oficial pendiente de licencia]',
        reverseScored: false,
        options: null
      },
      item9: {
        id: 9,
        domain: 'function_activity',
        text: '[Ítem 9 - Función: Texto oficial pendiente de licencia]',
        reverseScored: false,
        options: null
      },
      item10: {
        id: 10,
        domain: 'self_image',
        text: '[Ítem 10 - Autoimagen: Texto oficial pendiente de licencia]',
        reverseScored: false,
        options: null
      },
      item11: {
        id: 11,
        domain: 'pain',
        text: '[Ítem 11 - Dolor: Texto oficial pendiente de licencia]',
        reverseScored: false,
        options: null
      },
      item12: {
        id: 12,
        domain: 'function_activity',
        text: '[Ítem 12 - Función: Texto oficial pendiente de licencia]',
        reverseScored: false,
        options: null
      },
      item13: {
        id: 13,
        domain: 'mental_health',
        text: '[Ítem 13 - Salud Mental: Texto oficial pendiente de licencia]',
        reverseScored: false,
        options: null
      },
      item14: {
        id: 14,
        domain: 'self_image',
        text: '[Ítem 14 - Autoimagen: Texto oficial pendiente de licencia]',
        reverseScored: false,
        options: null
      },
      item15: {
        id: 15,
        domain: 'function_activity',
        text: '[Ítem 15 - Función: Texto oficial pendiente de licencia]',
        reverseScored: false,
        options: null
      },
      item16: {
        id: 16,
        domain: 'mental_health',
        text: '[Ítem 16 - Salud Mental: Texto oficial pendiente de licencia]',
        reverseScored: false,
        options: null
      },
      item17: {
        id: 17,
        domain: 'pain',
        text: '[Ítem 17 - Dolor: Texto oficial pendiente de licencia]',
        reverseScored: false,
        options: null
      },
      item18: {
        id: 18,
        domain: 'function_activity',
        text: '[Ítem 18 - Función: Texto oficial pendiente de licencia]',
        reverseScored: false,
        options: null
      },
      item19: {
        id: 19,
        domain: 'self_image',
        text: '[Ítem 19 - Autoimagen: Texto oficial pendiente de licencia]',
        reverseScored: false,
        options: null
      },
      item20: {
        id: 20,
        domain: 'mental_health',
        text: '[Ítem 20 - Salud Mental: Texto oficial pendiente de licencia]',
        reverseScored: false,
        options: null
      },
      item21: {
        id: 21,
        domain: 'satisfaction',
        text: '[Ítem 21 - Satisfacción: Texto oficial pendiente de licencia]',
        reverseScored: false,
        options: null
      },
      item22: {
        id: 22,
        domain: 'satisfaction',
        text: '[Ítem 22 - Satisfacción: Texto oficial pendiente de licencia]',
        reverseScored: false,
        options: null
      }
    }
  }
};

// Schema para el sistema de cuestionarios (compatible con EvaluacionWizard)
export const SRS22R_SCHEMA = {
  id: 'srs22r',
  name: 'SRS-22r (Escoliosis)',
  fullName: 'Scoliosis Research Society - 22 revised',
  description: 'Cuestionario específico para evaluar la calidad de vida en pacientes con escoliosis. Evalúa función, dolor, autoimagen, salud mental y satisfacción.',
  estimatedTime: '5-8 minutos',
  category: 'escoliosis',
  version: 'SRS-22r',
  requiresLicense: true,
  licenseNote: 'Este instrumento es propiedad de la Scoliosis Research Society. Uso sujeto a autorización.',
  isSRS22R: true, // Flag para detección especial en el wizard
  targetPopulation: 'Adolescentes y adultos con escoliosis',
  scoring: {
    type: 'domain_scores',
    method: 'mean',
    domains: 5,
    itemsPerDomain: [5, 5, 5, 5, 2],
    totalItems: 22,
    range: { min: 1, max: 5 },
    interpretation: {
      1.0: 'Muy afectado',
      2.0: 'Significativamente afectado',
      3.0: 'Moderadamente afectado',
      4.0: 'Levemente afectado',
      5.0: 'Sin afectación'
    }
  },
  // Definición de secciones para getAllQuestions() si se usa el wizard genérico
  sections: [
    {
      id: 'srs22r_all',
      title: 'Cuestionario SRS-22r',
      questions: Array.from({ length: 22 }, (_, i) => ({
        id: `item${i + 1}`,
        itemNumber: i + 1,
        type: 'srs22r_item',
        required: true,
        min: 1,
        max: 5
      }))
    }
  ]
};

/**
 * Calcula las puntuaciones del SRS-22r
 * @param {Object} answers - Objeto con respuestas { item1: 3, item2: 4, ... }
 * @param {Object} config - Configuración opcional
 * @returns {Object} Resultados calculados
 */
export const calculateSRS22RScore = (answers, config = SRS22R_CONFIG) => {
  const results = {
    domain_scores: {},
    total_score: null,
    subtotal_score: null, // Sin satisfacción
    missing_items_count: 0,
    missing_items: [],
    answered_items_count: 0,
    is_complete: false,
    interpretation: null
  };

  // Contar respuestas válidas y faltantes
  for (let i = 1; i <= 22; i++) {
    const itemKey = `item${i}`;
    const value = answers[itemKey];
    if (value === undefined || value === null) {
      results.missing_items_count++;
      results.missing_items.push(i);
    } else {
      results.answered_items_count++;
    }
  }

  results.is_complete = results.missing_items_count === 0;

  // Calcular puntuación por dominio
  Object.entries(SRS22R_DOMAINS).forEach(([domainKey, domain]) => {
    const domainAnswers = domain.items
      .map(itemNum => {
        const value = answers[`item${itemNum}`];
        if (value === undefined || value === null) return null;
        
        // Aplicar inversión si es necesario
        const itemConfig = config.texts.items[`item${itemNum}`];
        if (itemConfig?.reverseScored || SRS22R_REVERSE_ITEMS.includes(itemNum)) {
          return (config.maxValue + config.minValue) - value;
        }
        return value;
      })
      .filter(v => v !== null);

    if (domainAnswers.length > 0) {
      const sum = domainAnswers.reduce((a, b) => a + b, 0);
      const mean = sum / domainAnswers.length;
      results.domain_scores[domainKey] = {
        score: Math.round(mean * 100) / 100, // 2 decimales
        answered: domainAnswers.length,
        total: domain.items.length,
        percentage: Math.round((domainAnswers.length / domain.items.length) * 100)
      };
    } else {
      results.domain_scores[domainKey] = {
        score: null,
        answered: 0,
        total: domain.items.length,
        percentage: 0
      };
    }
  });

  // Calcular score total (sin satisfacción por defecto)
  const domainsForTotal = config.includeSatisfactionInTotal
    ? Object.keys(SRS22R_DOMAINS)
    : Object.keys(SRS22R_DOMAINS).filter(k => k !== 'satisfaction');

  const validDomainScores = domainsForTotal
    .map(k => results.domain_scores[k]?.score)
    .filter(s => s !== null && s !== undefined);

  if (validDomainScores.length > 0) {
    if (config.totalScoreMethod === 'domain_mean') {
      // Media de dominios
      const sum = validDomainScores.reduce((a, b) => a + b, 0);
      results.subtotal_score = Math.round((sum / validDomainScores.length) * 100) / 100;
    } else {
      // Media de todos los ítems (excepto satisfacción si no se incluye)
      const itemsForTotal = config.includeSatisfactionInTotal
        ? Array.from({ length: 22 }, (_, i) => i + 1)
        : Object.entries(SRS22R_DOMAINS)
            .filter(([k]) => k !== 'satisfaction')
            .flatMap(([, d]) => d.items);
      
      const validAnswers = itemsForTotal
        .map(i => answers[`item${i}`])
        .filter(v => v !== null && v !== undefined);
      
      if (validAnswers.length > 0) {
        const sum = validAnswers.reduce((a, b) => a + b, 0);
        results.subtotal_score = Math.round((sum / validAnswers.length) * 100) / 100;
      }
    }
  }

  // Score total incluyendo satisfacción
  const allDomainScores = Object.values(results.domain_scores)
    .map(d => d.score)
    .filter(s => s !== null && s !== undefined);

  if (allDomainScores.length > 0) {
    const sum = allDomainScores.reduce((a, b) => a + b, 0);
    results.total_score = Math.round((sum / allDomainScores.length) * 100) / 100;
  }

  // Interpretación
  const scoreForInterpretation = results.subtotal_score || results.total_score;
  if (scoreForInterpretation !== null) {
    if (scoreForInterpretation >= 4.5) {
      results.interpretation = 'Excelente calidad de vida';
    } else if (scoreForInterpretation >= 4.0) {
      results.interpretation = 'Buena calidad de vida';
    } else if (scoreForInterpretation >= 3.5) {
      results.interpretation = 'Calidad de vida aceptable';
    } else if (scoreForInterpretation >= 3.0) {
      results.interpretation = 'Calidad de vida moderada';
    } else if (scoreForInterpretation >= 2.5) {
      results.interpretation = 'Calidad de vida reducida';
    } else {
      results.interpretation = 'Calidad de vida significativamente afectada';
    }
  }

  return results;
};

/**
 * Valida las respuestas del SRS-22r
 * @param {Object} answers - Respuestas { item1: 3, ... }
 * @param {boolean} allowIncomplete - Permitir guardar incompleto
 * @returns {Object} { isValid, errors, warnings }
 */
export const validateSRS22RAnswers = (answers, allowIncomplete = false) => {
  const errors = [];
  const warnings = [];
  const config = SRS22R_CONFIG;

  for (let i = 1; i <= 22; i++) {
    const itemKey = `item${i}`;
    const value = answers[itemKey];

    if (value === undefined || value === null) {
      if (!allowIncomplete) {
        errors.push(`El ítem ${i} es obligatorio`);
      } else {
        warnings.push(`El ítem ${i} no ha sido respondido`);
      }
    } else if (value < config.minValue || value > config.maxValue) {
      errors.push(`El ítem ${i} debe estar entre ${config.minValue} y ${config.maxValue}`);
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    completeness: Math.round(((22 - errors.length - warnings.length) / 22) * 100)
  };
};

/**
 * Obtiene el color del dominio
 */
export const getDomainColor = (domainId) => {
  return SRS22R_DOMAINS[domainId]?.color || '#6B7280';
};

/**
 * Obtiene el nombre del dominio
 */
export const getDomainName = (domainId, short = false) => {
  const domain = SRS22R_DOMAINS[domainId];
  return short ? domain?.nameShort : domain?.name || domainId;
};

export default SRS22R_SCHEMA;
